/**
 * contractPdfHelper.js
 * Utility for multi-page contract PDF downloading and A4 printing without truncation or cutoff.
 */

// Dynamically load html2pdf.js if not already present
export const loadHtml2Pdf = () => {
  return new Promise((resolve, reject) => {
    if (window.html2pdf) {
      resolve(window.html2pdf);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
    script.onload = () => resolve(window.html2pdf);
    script.onerror = () => reject(new Error('html2pdf.js load failed'));
    document.head.appendChild(script);
  });
};

/**
 * Downloads a contract element as a multi-page PDF file (.pdf)
 */
export const downloadContractPdf = async (contractElement, contractTitle = '전자계약서') => {
  if (!contractElement) {
    alert('출력할 계약서 문서가 선택되지 않았습니다.');
    return;
  }

  const cleanTitle = (contractTitle || '전자계약서').replace(/[^a-zA-Z0-9가-힣_-]/g, '_');
  const filename = `${cleanTitle}_체결계약서.pdf`;

  // Create temporary off-screen container to isolate PDF rendering
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '800px';
  container.style.zIndex = '-9999';

  const clone = contractElement.cloneNode(true);
  // Strip non-printable UI buttons from clone
  clone.querySelectorAll('.no-print').forEach(el => el.remove());

  clone.style.padding = '20px';
  clone.style.maxWidth = '800px';
  clone.style.margin = '0 auto';
  clone.style.background = '#ffffff';
  clone.style.color = '#0f172a';
  container.appendChild(clone);
  document.body.appendChild(container);

  // 3.5s Safety Timeout Promise
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('PDF generation timeout')), 3500)
  );

  try {
    const html2pdf = await loadHtml2Pdf();

    // Options for html2pdf
    const opt = {
      margin: [12, 10, 12, 10], // top, left, bottom, right in mm
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: false, scrollY: 0 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // Race between html2pdf save and timeout
    await Promise.race([
      html2pdf().set(opt).from(clone).save(),
      timeoutPromise
    ]);
  } catch (err) {
    console.warn('html2pdf timeout or fallback to print window:', err);
    // Fallback to instant print window if html2pdf hangs or fails
    printContractDocument(contractElement, contractTitle);
  } finally {
    // Remove temporary container from DOM
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    // Clean up any leftover html2pdf overlay containers from body
    document.querySelectorAll('.html2pdf__container, .html2pdf__overlay').forEach(el => el.remove());

    // Force restore body pointer events & cursor
    document.body.style.pointerEvents = 'auto';
    document.body.style.cursor = 'default';
  }
};

/**
 * Opens a dedicated multi-page A4 print popup window for printing/Save as PDF
 */
export const printContractDocument = (contractElement, contractTitle = '전자계약서') => {
  if (!contractElement) {
    alert('출력할 계약서 문서가 선택되지 않았습니다.');
    return;
  }

  const printWindow = window.open('', '_blank', 'width=900,height=1000');
  if (!printWindow) {
    alert('팝업 차단이 설정되어 있습니다. 팝업 차단을 해제한 후 다시 시도해 주세요.');
    return;
  }

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="ko">
      <head>
        <meta charset="utf-8" />
        <title>${contractTitle} - 전자계약서 출력</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Pretendard:wght@400;600;700;800&display=swap');
          @page {
            size: A4 portrait;
            margin: 15mm 10mm 15mm 10mm;
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            background: #ffffff !important;
            color: #0f172a !important;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .contract-print-page {
            width: 100%;
            max-width: 800px;
            margin: 0 auto;
            background: #ffffff !important;
          }
          .avoid-break, .contract-signature-box {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .page-break {
            page-break-before: always !important;
            break-before: page !important;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body class="p-6">
        <div class="contract-print-page">
          ${contractElement.innerHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 600);
            }, 400);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
};
